<template>
  <div class="bg-gray-100 border-t border-primary/30 py-8 overflow-hidden">
    <div
      class="marquee-track"
      :class="directionClass"
      :style="{ '--marquee-duration': animationSpeed }"
    >
      <div class="marquee-set">
        <div
          v-for="(logo, i) in logos"
          :key="'a-' + i"
          class="logo-item flex-shrink-0 px-8 flex items-center"
          :class="{ 'cursor-pointer': logo.url }"
          :data-tooltip="logo.hoverText || null"
          @click="logo.url && openUrl(logo.url)"
        >
          <img
            v-if="logo.imageUrl"
            v-lazy-load :data-src="logo.imageUrl"
            :alt="logo.name"
            class="partner-logo"
          />
          <span v-else class="text-2xl font-bold text-gray-400 select-none whitespace-nowrap">
            {{ logo.name }}
          </span>
        </div>
      </div>
      <div class="marquee-set" aria-hidden="true">
        <div
          v-for="(logo, i) in logos"
          :key="'b-' + i"
          class="logo-item flex-shrink-0 px-8 flex items-center"
          :class="{ 'cursor-pointer': logo.url }"
          :data-tooltip="logo.hoverText || null"
          @click="logo.url && openUrl(logo.url)"
        >
          <img
            v-if="logo.imageUrl"
            v-lazy-load :data-src="logo.imageUrl"
            :alt="logo.name"
            class="partner-logo"
          />
          <span v-else class="text-2xl font-bold text-gray-400 select-none whitespace-nowrap">
            {{ logo.name }}
          </span>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { computed, onMounted, useContext } from "@nuxtjs/composition-api";
import useModuleContent from "~/composables/useModuleContent";

export default {
  name: "PartnerLogosModule",
  props: {
    moduleConfig: {
      type: Object,
      default: () => ({}),
    },
  },
  setup(props) {
    const { $posthog } = useContext();
    const { content } = useModuleContent(props);

    const logos = computed(() => {
      const base = content.value.logos || [];
      if (!base.length) return [];
      // Ensure enough logos to fill the viewport for seamless scrolling
      const repeats = Math.max(1, Math.ceil(10 / base.length));
      const result = [];
      for (let i = 0; i < repeats; i++) {
        result.push(...base);
      }
      return result;
    });

    const directionClass = computed(() => {
      return content.value.direction === "left" ? "scroll-left" : "scroll-right";
    });

    const animationSpeed = computed(() => {
      const speed = parseInt(content.value.speed, 10) || 20;
      return `${speed}s`;
    });

    onMounted(() => {
      $posthog?.capture("front_page_module_viewed", {
        module_slug: props.moduleConfig.slug,
        module_position: props.moduleConfig.position,
        module_row: props.moduleConfig.row,
        version_id: props.moduleConfig.versionId,
      });
    });

    const openUrl = (url) => {
      window.open(url, "_blank", "noopener,noreferrer");
    };

    return { content, logos, directionClass, animationSpeed, openUrl };
  },
};
</script>

<style scoped>
.partner-logo {
  height: 3rem;
  width: auto;
  filter: grayscale(100%);
  opacity: 0.7;
  transition: all 0.2s ease;
}
.partner-logo:hover {
  filter: grayscale(0%);
  opacity: 1;
}

.logo-item {
  position: relative;
}
.logo-item[data-tooltip]::after {
  content: attr(data-tooltip);
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  padding: 4px 10px;
  background: rgba(0, 0, 0, 0.8);
  color: #fff;
  font-size: 0.75rem;
  line-height: 1.4;
  border-radius: 4px;
  white-space: nowrap;
  pointer-events: none;
  opacity: 0;
  transition: opacity 0.15s ease;
}
.logo-item[data-tooltip]:hover::after {
  opacity: 1;
}

.marquee-track {
  display: flex;
  width: max-content;
  animation-timing-function: linear;
  animation-iteration-count: infinite;
  animation-duration: var(--marquee-duration, 20s);
}

.marquee-track:hover {
  animation-play-state: paused;
}

.marquee-set {
  display: flex;
  flex-shrink: 0;
}

.scroll-left {
  animation-name: marquee-left;
}

.scroll-right {
  animation-name: marquee-right;
}

@keyframes marquee-left {
  0% {
    transform: translateX(0);
  }
  100% {
    transform: translateX(-50%);
  }
}

@keyframes marquee-right {
  0% {
    transform: translateX(-50%);
  }
  100% {
    transform: translateX(0);
  }
}
</style>
